import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles/DMSCard.scss';
import DMSDropdown from './DMSDropdown/DMSDropdown.js';
import {
  getDMSAccountDetailsFromEIS,
  removeDMS,
  showAllDMSAccountsLocations,
  updateDMS,
  updateDMSAccountsForAllLocations,
  updateDMSAccountsOnSelection,
  updateSelectedDMSAccounts
} from '../../../../../redux/connectedComponent/selectedDMS/selectedDMS.slice';
import {
  updateDMSAccountsDetailsForApp
} from '../../../../../redux/connectedComponent/updateDMSAccounts/updateDMSAccounts.slice';
import AuthComponent from '../../../../common/AuthComponent';
import { Button, Loader } from 'cdk-radial';
import Notification from '../../../../notification/Notification';

const { DMS_ACCOUNT_TYPES_MAPPING } = require('./utils/constant');

const getDMSAccountHeader = (dmsType, apiId, dmsApiDetails, navigatedFromApiIntegrations) => {

  if(navigatedFromApiIntegrations) {
    //only show the dms type required for the selected api
    let dmsTypesRequiredForApi = getDMSTypesAsPerApi(apiId, dmsApiDetails);
    if(dmsTypesRequiredForApi.includes(dmsType)) {
      return getDMSTypeHeader(dmsType);
    }
    else return;
  }

  return getDMSTypeHeader(dmsType);
}

const getDMSTypeHeader = (dmsType) => {
  if(Object.entries(DMS_ACCOUNT_TYPES_MAPPING).filter(ele => ele[1] === dmsType).length) {
    return `${dmsType} DMS Account`;
  }
}

const getApiNamesForDmsType = (dmsType, dmsApiDetails, apiDetails) => {
  const apiNames = [];
  for (const [apiId, dmsTypesContainer] of Object.entries(dmsApiDetails)) {
    const dmsTypes = dmsTypesContainer.flat();
    if (dmsTypes.includes(dmsType)) {
      apiNames.push(apiDetails?.find(a => a.id === apiId)?.displayName);
    }
  }
  return apiNames.join(', ');
};

const getDMSTypesAsPerApi = (apiId, dmsApiDetails) => {
  //check dms account types required for the app - service, parts...
  /*
  format from dmsApiDetails:
  {
    "(API ID)": [
      [                    <-- double deep array
        "(DMS ACCT TYPE)",
        "(DMS ACCT TYPE)",
        ...
      ]
    ],
    ...
  }
   */
  return dmsApiDetails?.[apiId]?.flat() || [];
}

const DMSAccountSelectionCardContent = props => {
  const dispatch = useDispatch();
  const state = useSelector(state => state);

  const [enableSave, setEnableSave] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  const {
    selectedDMS,
    showAllLocations,
    updatedSelectedLocations,
    DMSConfigInfo,
    updatedSelectedAllLocations,
    dmsApiDetails,
    getEISDMSInfo,
    getDMSInfoBasedOnApisPending,
    isEISDMSInfoPending
  } = state.selectDMSSlice;

  const { isDMSAccountsUpdateLoading, isDMSAccountsUpdateSuccess, errorInDMSAccountsUpdate } = state.updateDMSAccountsSlice;

  const { selectedLocationsWithCMFNumber, locationsToAddForSubManagementWithCMF }  = state.selectLocationsSlice;
  const { authenticationToken, allLocations, entityIdAddressMapping } = state.loginDetailsSlice;


  // solutionData prop is passed when we use this screen from integration management
  const solutionInfo = props.solutionData || state.solutionInfoSlice.solutionInfo;
  const apiDetails = solutionInfo?.registration_details?.api_details;

  const getDMSInfoForLocationsToBeEnabled = () => {
    if (!getDMSInfoBasedOnApisPending) {
      Promise.all([
        props.locationsToBeEnabled.map(locationData => {
          let entity = locationData && locationData.entityIdList && locationData.entityIdList[0];
          const location = allLocations.filter(loc => loc.entityId === locationData.entityId);
          dispatch(getDMSAccountDetailsFromEIS({
            location: (entity && entity.location) || location[0].partyName,
            cmfNumber: (entity && entity.cmfNumber) || location[0].cmfNumber,
            storeId: (entity && entity.storeId) || location[0].storeId,
            entityId:( entity && entity.entityId) || locationData.entityId,
            token: authenticationToken,
            dmsApiDetails: dmsApiDetails
          }))
        })
      ]);
    }
  }

  const filterBySelectedLocations = (
    dmsEISInfo,
    selectedLocationsDetails
  ) => {
    return dmsEISInfo.filter(el => {
      return selectedLocationsDetails.find(element => {
        let entity = element && element.entityIdList && element.entityIdList[0];
        if (entity) {
          return (
              entity.entityId === el.entityId && entity.cmfNumber === el.cmfNumber
          );
        } else {
          return element.entityId === el.entityId;
        }
      });
    });
  };

  useEffect(() => {
    if (props.navigatedFromApiIntegrations) {
      getDMSInfoForLocationsToBeEnabled();
    }
  }, []);

  useEffect(() => {
    if (props.navigatedFromApiIntegrations) {
      const updatedSelectedDMSAccounts = filterBySelectedLocations(
        getEISDMSInfo,
        props.locationsToBeEnabled
      );
      dispatch(updateSelectedDMSAccounts(updatedSelectedDMSAccounts));
    }
  }, [getEISDMSInfo]);

  useEffect(() => {
    const numberOfAccountTypes = Object.keys(showAllLocations).length;
    const numberOfSelectedLocations = props.navigatedFromSubscriptionManagement ? locationsToAddForSubManagementWithCMF.length : selectedLocationsWithCMFNumber.length;
    if ((numberOfAccountTypes * numberOfSelectedLocations) !== selectedDMS.length) {
      props.disableNextButton(true);
    } else {
      props.disableNextButton(false);
    }
  },[selectedDMS]);

  useEffect(() => {
    if (props.navigatedFromApiIntegrations) {
      const updatedAllLocations = Object.entries(DMS_ACCOUNT_TYPES_MAPPING).reduce((updatedLocation, [acctTypeSuffix, acctTypeName]) => {
        const updatedSelectedLocationsCopy = updatedSelectedLocations;

        const dmsAccountsArray = updatedSelectedLocations
          .map(item => ({
            entityId: item.entityId,
            dmsAccount: item.dmsAccount,
            departments: item.departments
          }))
          // take only the first entry for each entityId (store) - each store has its own unique set of departments
          .filter((val, index, arr) => arr.findIndex(t => (t.entityId === val.entityId)) === index);

        let updatedDMSAccounts = [];
        updatedSelectedLocationsCopy.forEach(item => {
          let updatedDMSAccountWithType = { ...item, type: acctTypeName }
          if (
            updatedDMSAccountWithType.dmsAccountType === acctTypeSuffix &&
            !updatedDMSAccounts.some(ele => ele.entityId === updatedDMSAccountWithType.entityId)
          ) {
            updatedDMSAccounts.push(updatedDMSAccountWithType);
          }
        });
        updatedSelectedLocationsCopy.map(updatedLocation => {
          let updatedDMSAccountWithType = { ...updatedLocation, type: acctTypeName }
          if (
            updatedDMSAccountWithType.dmsAccountType !== acctTypeSuffix &&
            !updatedSelectedLocationsCopy.some(item => item.dmsAccountType === acctTypeSuffix)
          ) {
            Object.entries(dmsApiDetails).forEach(([apiId])=>{
              if (dmsApiDetails[apiId] && dmsApiDetails[apiId][0] && updatedDMSAccountWithType.type === dmsApiDetails[apiId][0])
                updatedDMSAccountWithType.api = apiId;
            });
            Object.entries(DMS_ACCOUNT_TYPES_MAPPING).forEach(([key, value]) => {
              if (updatedDMSAccountWithType.type === value)
                updatedDMSAccountWithType.dmsAccountType = key;
            })
            updatedDMSAccountWithType.id = updatedDMSAccountWithType.id.substr(0,11) + Math.random().toString(36).substr(2, 9);
            const dmsArray = dmsAccountsArray.find(item => item.entityId === updatedDMSAccountWithType.entityId);
            updatedDMSAccountWithType = {
              ...updatedDMSAccountWithType,
              dmsAccount: dmsArray?.dmsAccount || []
            }
            if (!updatedDMSAccounts.some(item => item.entityId === updatedDMSAccountWithType.entityId)) {
              updatedDMSAccounts.push(updatedDMSAccountWithType);
            }
          }
        });
        updatedLocation[acctTypeName] = updatedDMSAccounts;
        return updatedLocation;
      }, {});
      dispatch(updateDMSAccountsForAllLocations(updatedAllLocations));
    }
  }, [updatedSelectedLocations]);

  useEffect(() => {
    if(props.navigatedFromApiIntegrations && DMSConfigInfo && DMSConfigInfo.length){
      const filteredCardsByApiIds = Object.keys(updatedSelectedAllLocations)
      .filter(key => DMSConfigInfo.includes(key))
      .reduce((obj, key) => {
        obj[key] = updatedSelectedAllLocations[key];
        return obj;
      }, {});
      dispatch(showAllDMSAccountsLocations(filteredCardsByApiIds));
    }
  }, [updatedSelectedAllLocations, DMSConfigInfo]);

  useEffect(() => {
    selectedDMS && selectedDMS.length && setEnableSave(true);
  }, [selectedDMS])

  useEffect(() => {
    if (props.navigatedFromApiIntegrations) {
      const flattenedLocations = Object.entries(showAllLocations).reduce((newObj, [key, value]) => {
        const v = value.map(item => {
          return Object.assign({}, item, {
            dmsAccount: item.dmsAccount[0],
            departments: [item.departments[0]]
          });
        });
        newObj = [ ...newObj, ...v];
        return newObj;
      }, []);
      const filteredLocationsWithDMSAccounts = flattenedLocations && flattenedLocations.filter(location => location.dmsAccount);
      dispatch(updateDMSAccountsOnSelection(filteredLocationsWithDMSAccounts));
    }
  }, [showAllDMSAccountsLocations, showAllLocations]);

  const handleSelectDMS = (location, dmsAccount, departments, cmfNumber, dmsAccountType, entityId, type, id, api) => {
    if (dmsAccount === '') {
      dispatch(removeDMS({ location, dmsAccount, departments, cmfNumber, dmsAccountType, entityId, type, id, api }));
    }
    else {
      dispatch(updateDMS({ location, dmsAccount, departments, cmfNumber, dmsAccountType, entityId, type, id, api}));
    }
  };

  const saveDMS = () => {
    let selectedDMSWithApi = selectedDMS && selectedDMS.length && selectedDMS.map(dms => ({
      ...dms,
      api: props.apiId
    }));

    let { isBulkSubscribed, isAsyncApi, onDMSSelected } = props;

    if(isBulkSubscribed && isAsyncApi) {
      onDMSSelected(selectedDMSWithApi);
    }
    else {
      dispatch(updateDMSAccountsDetailsForApp({
        token: authenticationToken,
        solutionId:props.solutionId,
        updatedDMSAcounts: selectedDMSWithApi,
        isNewAPI: props.isNewApi
      }));
    }
  }

  const renderDealerships = (dmsType, dmsAccountDetails, apiId) => {
    if(props.navigatedFromApiIntegrations) {
      //check if the selected api requires the dms type
      let dmsTypesRequiredForApi = getDMSTypesAsPerApi(apiId, dmsApiDetails);
      if(dmsTypesRequiredForApi.includes(dmsType)) {
        return renderDMSForDealerships(dmsAccountDetails);
      }
      else return;
    }

    return renderDMSForDealerships(dmsAccountDetails);
  };

  const renderDMSForDealerships = (dmsAccountDetails) => {
    const filteredLocations = [
      ...new Map(dmsAccountDetails.map(item => [item.entityId, item])).values()
    ];
    const filterLocationWithDMSAccounts = dmsAccountDetails.filter(location => location.dmsAccount);

    if (filterLocationWithDMSAccounts.length !== filteredLocations.length) {
      filteredLocations.forEach(item => {
        if (!filterLocationWithDMSAccounts.some(element => item.entityId === element.entityId)) {
          filterLocationWithDMSAccounts.push(item);
        }
      })
    }

    return filterLocationWithDMSAccounts.map(locationData => {
      let { location, dmsAccount, cmfNumber, dmsAccountType, departments, entityId, type, id, api } = locationData;
      let object = selectedDMS.find(item => {
        return (
          item.entityId === entityId &&
          item.id === id &&
          item.type === type
        );
      });

      return (
        <DMSDropdown
          key={`${location}-${cmfNumber}-${dmsAccountType}-${id}`}
          location={location}
          address={entityIdAddressMapping[entityId]}
          cmfNumber={cmfNumber}
          dmsAccount={dmsAccount}
          dmsAccountType={dmsAccountType}
          departments={departments}
          entityId={entityId}
          handleSelectDMS={handleSelectDMS}
          id={id}
          selectedDMS={object && object.dmsAccount}
          selectedDepartments={object && object.departments}
          type={type}
          disableSave={()=>{}}
          api={api}
        />
      );
    });
  }

  if(isEISDMSInfoPending && props.navigatedFromApiIntegrations) {
    return (
      <div className="dms-loader">
        <Loader label="Fetching DMS Accounts Details..." />
      </div>
    )
  }

  if(isDMSAccountsUpdateLoading && props.navigatedFromApiIntegrations) {
    return (
      <div className="dms-loader">
        <Loader label="Saving DMS Accounts Details..." />
      </div>
    )
  }

  if(isDMSAccountsUpdateSuccess && props.navigatedFromApiIntegrations) {
    setTimeout(() => {
      props.onClose(true);
    }, 2000);
    return (
      <Notification
        content={'DMS Account(s) Saved Successfully'}
        variant={'positive'}
        actions={[{"text":"Dismiss"}]}
        onDismiss={() => setShowNotification(false)}
        customStyle={{ marginTop: '70px' }}
      />
    )
  }

  if(errorInDMSAccountsUpdate && props.navigatedFromApiIntegrations) {
    setTimeout(() => {
      props.onClose(false);
    }, 2000);
    return (
      <Notification
        content={'Failed To Save DMS Accounts'}
        variant={'negative'}
        actions={[{"text":"Dismiss"}]}
        onDismiss={() => setShowNotification(false)}
        customStyle={{ marginTop: '70px' }}
      />
    )
  }

  const renderDMSCards = (key, value) => {
    return (
      <div className="select-dms-account-container" key={key}>
        <div className="dms-header">
          <div className="dms-header-title">
            {getDMSAccountHeader(key, props.apiId, dmsApiDetails, props.navigatedFromApiIntegrations)}
          </div>
          <div className="dms-card-header-subtitle">
            {getApiNamesForDmsType(key, dmsApiDetails, apiDetails)}
          </div>
        </div>
        {renderDealerships(key, value, props.apiId)}
      </div>
    )
  }

  return (showAllLocations &&
      <div>
        {
          //key = service, parts...
          Object.entries(showAllLocations).map(([key, value]) =>
            {
              if(props.navigatedFromApiIntegrations) {
                //check if the selected api requires the dms type
                let dmsTypesRequiredForApi = getDMSTypesAsPerApi(props.apiId, dmsApiDetails);
                if(dmsTypesRequiredForApi.includes(key)) {
                  return renderDMSCards(key, value)
                }
              }
              else {
                return renderDMSCards(key, value)
              }
            }
        )}
        {
        props.navigatedFromApiIntegrations &&
          <div className='dms-save-container'>
            <Button
              backgroundAppearance="light"
              iconColor="primary"
              iconSize={24}
              isDisabled={!enableSave}
              onClick={saveDMS}
              size="standard"
              text="Save"
            />
          </div>
        }
      </div>
  );
};

export default AuthComponent(DMSAccountSelectionCardContent);
